import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import Spinner from '@/components/Common/Spinner';
import Empty from '@/components/Empty/Empty';
import { Stage } from '@/components/Stage/Stage';
import {
  CHART_CALCULATION,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import PieChart from '@/features/(charts)/chart-renderer/ui/Single/PieChart';
import { convertSignalDatePresetToChartTerm } from '@/features/(signal)/signals/lib';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '@/features/(signal)/signals/model';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import service from '@/services';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';

export const SentimentPieChartArea = (props: {
  issue: CompositedSignalIssue;
  datePreset: SignalDateSelectionInput;
  filterSet: StructuredFilter;
}) => {
  const processedFilter = useMemo(() => {
    const sFilter = new SFilter(props.filterSet);
    sFilter.removeField(FEEDBACK_COLUMNS.DATE_START);
    sFilter.removeField(FEEDBACK_COLUMNS.DATE_END);
    return sFilter.get() || {};
  }, [props.filterSet]);

  const chart: ChartBase & { name: string } = {
    name: 'sentiment-pie-chart',
    field: {
      type: CHART_FIELD.FEEDBACK_SENTIMENT_DETAIL,
      entire: true,
    },
    graph: CHART_GRAPH.PIE,
    graph_direction: CHART_GRAPH_DIRECTION.VERTICAL,
    historical: {
      frequency: null,
      value: false,
    },
    type: CHART_TYPE.SINGLE_AXIS,
    selected_field: {
      method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_10,
      items: [],
    },
    target: {
      filter: processedFilter,
      term: convertSignalDatePresetToChartTerm(props.datePreset),
    },
    series: [
      {
        measure: {
          metric: CHART_METRIC.FEEDBACK,
          calculation: CHART_CALCULATION.COUNT,
        },
      },
    ],
  };

  const { data, isFetching } = useQuery({
    queryKey: ['sentiment-pie-chart', chart],
    queryFn: () => service.chart.fetchTable(chart),
  });

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-2xl font-semibold">Sentiments</h3>
      <Stage className="h-75 w-full relative p-4 rounded-2xl border border-border">
        <Stage.Scene
          status={isFetching}
          className="flex items-center justify-center"
        >
          <Spinner />
        </Stage.Scene>
        <Stage.Scene status={!isFetching && data?.tables?.[0]?.length === 0}>
          <Empty
            title="No data available"
            className="h-full"
            description="There is currently no data available to generate a pie chart."
          />
        </Stage.Scene>
        <Stage.Scene status={!isFetching} data={data}>
          {(data) => <PieChart chart={chart} tableData={data} />}
        </Stage.Scene>
      </Stage>
    </div>
  );
};
